/**
 * Presentational atoms shared by the City, Assets and Graph surfaces.
 *
 * Kept in one place so a status dot means the same thing everywhere.
 */
import { Link } from 'react-router-dom'

import { Priority } from '../../components/bits.jsx'

import { ASSET_STATUS_META, STATUSES, STATUS_META, cls } from './shared.js'

/** A coloured status pill for a ticket status. */
export function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status, color: 'var(--text-muted)' }
  return (
    <span className="chip" title={`status: ${status}`}>
      <span className="pm-dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  )
}

/** A coloured pill for an asset status. */
export function AssetStatusChip({ status }) {
  const meta = ASSET_STATUS_META[status] || { label: status, color: 'var(--text-muted)' }
  return (
    <span className="chip" title={`status: ${status}`}>
      <span className="pm-dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  )
}

/** P0..P3 — the shared board/list priority mark, re-exported for these views. */
export function PriorityTag({ priority }) {
  return <Priority value={priority} />
}

/** A neutral chip, used for kind / component / city tags. */
export function Tag({ children, title }) {
  return (
    <span className="chip" title={title}>
      {children}
    </span>
  )
}

/**
 * One dense ticket row: key, priority, title, and its kind / city tags.
 * Links to the ticket detail route (`/t/:key`).
 */
export function TicketRow({ ticket, showStatus = false, showCities = false }) {
  return (
    <Link className="pm-ticket" to={`/t/${ticket.key}`} title={ticket.title}>
      <span className="key">{ticket.key}</span>
      <PriorityTag priority={ticket.priority} />
      <span className="pm-ticket-title">{ticket.title}</span>
      <span className="pm-ticket-tags">
        {ticket.kind && ticket.kind !== 'task' ? <Tag title="kind">{ticket.kind}</Tag> : null}
        {showCities
          ? (ticket.cities || []).map((c) => (
              <Tag key={c.id} title="city">
                {c.slug}
              </Tag>
            ))
          : null}
        {showStatus ? <StatusChip status={ticket.status} /> : null}
      </span>
    </Link>
  )
}

/**
 * A one-line stacked bar of per-status counts.
 *
 * @param {object} counts  `{ status: n }` as returned by the city summary.
 */
export function StatusBar({ counts }) {
  const total = STATUSES.reduce((sum, s) => sum + (counts[s] || 0), 0)
  if (!total) return <div className="pm-statusbar" />
  return (
    <div className="pm-statusbar" title={STATUSES.map((s) => `${s}: ${counts[s] || 0}`).join('  ')}>
      {STATUSES.filter((s) => counts[s]).map((s) => (
        <span
          key={s}
          style={{
            width: `${((counts[s] || 0) / total) * 100}%`,
            background: STATUS_META[s].color,
          }}
        />
      ))}
    </div>
  )
}

/** Loading / error / empty placeholder used by every panel here. */
export function Placeholder({ loading, error, empty, children }) {
  if (loading) return <div className="pm-empty">Loading…</div>
  if (error) return <div className="pm-error">{String(error.message || error)}</div>
  if (empty) return <div className="pm-empty">{children || 'Nothing here.'}</div>
  return null
}

/** A card with a title, an optional right-hand slot, and a body. */
export function Card({ title, right, children, bodyClass }) {
  return (
    <section className="pm-card">
      {title ? (
        <header className="pm-card-head">
          <span>{title}</span>
          {right}
        </header>
      ) : null}
      <div className={cls(bodyClass !== undefined ? bodyClass : 'pm-card-body')}>{children}</div>
    </section>
  )
}
